
import { authConfig } from '../config/auth';
import { tokenManager, userManager } from './auth';

/**
 * Global API error handler
 * Handles token expiration and forces logout on authentication errors
 */
export const handleApiError = (response: Response): void => {
  if (response.status === 401) {
    // Don't clear tokens — the refresh cookie may still be valid.
    // Redirect to login so initAuth() can attempt a refresh; if it succeeds,
    // PublicRoute will send the user back to their original page automatically.
    const currentPath = window.location.pathname;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }
  // 403 Forbidden is an authorization error, not authentication — don't touch tokens.
};

/**
 * Wrapper for fetch that automatically handles auth errors
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const response = await fetch(url, options);
  
  // Check for auth errors
  if (response.status === 401) {
    handleApiError(response);
    throw new Error('Authentication failed. Please log in again.');
  }
  
  return response;
};

/**
 * Error codes the API returns when the signed-in account has lost access
 * (see backend utils/access.ts). Any of these signs the user out.
 */
const DEACTIVATION_CODES = ['AccountDeactivated', 'CompanyDeactivated', 'AccountDeleted'];

/** sessionStorage key for the reason shown on the login page after a forced sign-out. */
export const SIGNED_OUT_NOTICE_KEY = 'signed_out_notice';

function signOutForDeactivation(code: string, message?: string): void {
  tokenManager.clearTokens();
  userManager.clearUser();
  // A deleted account gets the plain login form — no explanation needed.
  if (code !== 'AccountDeleted' && message) {
    try {
      sessionStorage.setItem(SIGNED_OUT_NOTICE_KEY, message);
    } catch {
      // Storage unavailable (private mode etc.) — the user still lands on login.
    }
  }
  window.location.href = '/login';
}

/**
 * Setup global fetch interceptor. Watches every API response for the
 * deactivation codes above — an archived/deleted account or a canceled company —
 * and signs the user out with an explanation on the login page. This covers
 * every page without each one handling it.
 */
export const setupGlobalErrorHandler = (): void => {
  // Network errors (Failed to fetch, wifi drops, etc.) must never clear auth state —
  // the refresh token cookie is likely still valid and will work when connectivity returns.
  // Token cleanup is handled by refreshAuthTokens() on permanent Cognito rejections only.
  if ((window as any).__crewcastFetchPatched) return;
  (window as any).__crewcastFetchPatched = true;

  const originalFetch = window.fetch.bind(window);
  let signingOut = false;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);

    if ((response.status === 401 || response.status === 403) && !signingOut) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      // The login form shows its own error for these codes, so skip it here.
      if (url.startsWith(authConfig.apiUrl) && !url.includes('/api/auth/login')) {
        try {
          const data = await response.clone().json();
          if (DEACTIVATION_CODES.includes(data?.error)) {
            signingOut = true;
            signOutForDeactivation(data.error, data.message);
          }
        } catch {
          // Non-JSON body — not a deactivation response.
        }
      }
    }

    return response;
  };
};

